import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";
import { isOutOfRange } from "../pages/PathologyResultApproval/pathologyResultApprovalData";

const WEB_BASE = "/api/v1/web/pathology-results";

const formatDisplayTime = (timeValue) => {
  if (!timeValue) return "";
  const raw = String(timeValue);
  const timePart = raw.includes("T") ? raw.split("T")[1] : raw;
  const hhmmss = timePart.slice(0, 8);
  const match = hhmmss.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return raw;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
};

/** Map UI filter radios → API ints: patientType 0=All,1=OP,2=IP; reportStatus 0=All,1=Pending,2=Completed */
export const mapFiltersToApiParams = (filters = {}) => {
  const patientType =
    filters.patientType === "OP" ? 1 : filters.patientType === "IP" ? 2 : 0;
  const reportStatus =
    filters.reportStatus === "Pending"
      ? 1
      : filters.reportStatus === "Completed"
        ? 2
        : 0;
  return {
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    search: (filters.search || "").trim() || undefined,
    uhid: (filters.uhid || "").trim() || undefined,
    patientType,
    reportStatus,
  };
};

export const mapListItemToUi = (row) => ({
  id: row.pathReportId,
  pathReportId: row.pathReportId,
  date: row.date || "",
  time: formatDisplayTime(row.time),
  uhid: row.uhid || "",
  patientName: (row.patientName || "").replace(/\s+/g, " ").trim(),
  contact: row.contact || "",
  genderAge: row.genderAge || "",
  refDoctor: row.refDoctor || "-",
  testName: row.testName || "",
  doctorName: row.doctorName || "",
  patientType: row.patientType || "OP",
  reportStatus: row.reportStatus || "Pending",
  printed: Boolean(row.printed),
  approved: Boolean(row.approved),
  sampleNumber: row.sampleNumber || "",
  parameters: [],
});

export const mapDetailToUi = (detail, pathologistOptions = [], doctorOptions = []) => {
  const header = detail?.header || {};
  const findOption = (options, id) => {
    if (!id) return null;
    return (
      options.find((o) => Number(o.id) === Number(id) || Number(o.value) === Number(id)) || {
        id,
        value: id,
        label: String(id),
      }
    );
  };

  return {
    id: header.pathReportId,
    pathReportId: header.pathReportId,
    date: header.date || "",
    time: formatDisplayTime(header.time),
    uhid: header.uhid || "",
    patientName: (header.patientName || "").replace(/\s+/g, " ").trim(),
    contact: header.contact || "",
    genderAge: header.genderAge || "",
    refDoctor: header.refDoctor || "-",
    testName: header.testName || "",
    doctorName: header.doctorName || "",
    patientType: header.patientType || "OP",
    reportStatus: header.reportStatus || "Pending",
    printed: Boolean(header.printed),
    approved: Boolean(header.approved),
    sampleNumber: header.sampleNumber || "",
    pathologist: findOption(pathologistOptions, header.pathologistId),
    doctor: findOption(doctorOptions, header.doctorId),
    labId: header.labId
      ? { id: header.labId, value: header.labId, label: header.labId }
      : null,
    suggestionNote: header.suggestionNote || "",
    digitalSignRequired: Boolean(header.digitalSignRequired),
    parameters: (detail?.parameters || []).map((p) => ({
      id: p.pathReportDetId || `p-${p.parameterId}-${p.testId}-${p.subTestId}`,
      pathReportDetId: p.pathReportDetId || 0,
      testId: p.testId || 0,
      subTestId: p.subTestId || 0,
      parameterId: p.parameterId || 0,
      testName: p.testName || header.testName || "",
      subtestName: p.subtestName || "-",
      parameterName: p.parameterName || "",
      result: p.result || "",
      normalRange: p.normalRange || "",
      isAbnormal: Boolean(p.isAbnormal),
    })),
  };
};

export const mapUiToSavePayload = (record, userId) => {
  const pathologistId = Number(
    record?.pathologist?.value ?? record?.pathologist?.id ?? 0
  );
  const doctorId = Number(record?.doctor?.value ?? record?.doctor?.id ?? 0);
  const labId =
    record?.labId?.value ?? record?.labId?.id ?? record?.labId ?? "";

  return {
    pathologistId: Number.isFinite(pathologistId) ? pathologistId : 0,
    doctorId: Number.isFinite(doctorId) ? doctorId : 0,
    labId: labId != null ? String(labId) : "",
    suggestionNote: record?.suggestionNote || "",
    digitalSignRequired: Boolean(record?.digitalSignRequired),
    userId: Number(userId || 1),
    parameters: (record?.parameters || []).map((p) => ({
      pathReportDetId: Number(p.pathReportDetId || 0),
      parameterId: Number(p.parameterId || 0),
      testId: Number(p.testId || 0),
      subTestId: Number(p.subTestId || 0),
      result: p.result != null ? String(p.result) : "",
      normalRange: p.normalRange != null ? String(p.normalRange) : "",
      isAbnormal:
        Boolean(p.isAbnormal) || isOutOfRange(p.result, p.normalRange),
    })),
  };
};

export const listPathologyResults = async (filters = {}) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, {
      params: mapFiltersToApiParams(filters),
    });
    return (response.data?.data ?? []).map(mapListItemToUi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load pathology results"));
  }
};

export const getPathologyResultById = async (pathReportId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/${pathReportId}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load pathology result"));
  }
};

export const savePathologyResult = async (pathReportId, payload) => {
  try {
    const response = await axiosInstance.put(`${WEB_BASE}/${pathReportId}`, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to save pathology result"));
  }
};

export const approvePathologyResult = async (pathReportId, userId = 1) => {
  try {
    const response = await axiosInstance.post(
      `${WEB_BASE}/${pathReportId}/approve`,
      null,
      { params: { userId } }
    );
    return response.data?.data;
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to approve pathology result"));
  }
};

export const listPathologyLabs = async () => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/labs`);
    return (response.data?.data ?? []).map((row) => ({
      id: row.labId,
      value: row.labId,
      label: row.labId,
    }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load labs"));
  }
};
